import { Component, DestroyRef, ElementRef, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import {
  YdsButtonComponent,
  YdsButtonBackComponent,
  YdsCardEditableComponent,
  YdsComboboxComponent,
  YdsDatePickerComponent,
  YdsSpinnerComponent,
  YdsSwitchComponent,
  YdsTextAreaComponent,
  YdsTextFieldComponent,
  type YdsComboboxOption,
} from '../../../yds-angular';
import {
  defaultEndDate,
  defaultStartDate,
  firstDayOfMonthFilter,
  formatDisplayDate,
  isInvalid,
  lastDayOfMonthFilter,
  startBeforeEndValidator,
  toFormDate,
} from '../../../shared/general-ledger-yds.utils';
import { JournalProcessDefinitionService } from '../journalprocessdefinition.service';
import { EmploymentSelectionService } from '../employmentselection.service';
import { BookingGroupService } from '../../bookinggroup/bookinggroup.service';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';
import { deleteJournalProcessDefinitionWithConfirm } from '../journalprocessdefinition-shared';
import { JournalProcessDefinition } from '../../../model/journal-process-definition.model';
import { EmploymentSelection } from '../../../model/employment-selection.model';

const MAX_END_DATE_ISO = '9999-12-31';

@Component({
  selector: 'app-journalprocessdefinition-editable',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    YdsCardEditableComponent,
    YdsButtonComponent,
    YdsButtonBackComponent,
    YdsSpinnerComponent,
    YdsSwitchComponent,
    YdsComboboxComponent,
    YdsDatePickerComponent,
    YdsTextAreaComponent,
    YdsTextFieldComponent,
  ],
  templateUrl: './journalprocessdefinition-editable.component.html',
  styleUrl: './journalprocessdefinition-editable.component.scss',
})
export class JournalprocessdefinitionEditableComponent {
  readonly isInvalid = isInvalid;
  readonly firstDayOfMonthFilter = firstDayOfMonthFilter;
  readonly lastDayOfMonthFilter = lastDayOfMonthFilter;
  readonly translate = inject(TranslateService);

  private formBuilder = inject(FormBuilder);
  private journalProcessDefinitionService = inject(JournalProcessDefinitionService);
  private employmentSelectionService = inject(EmploymentSelectionService);
  private bookingGroupService = inject(BookingGroupService);
  private dialogService = inject(ConfirmDialogService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  private readonly detailsCard = viewChild('detailsCard', { read: ElementRef<HTMLElement> });
  private readonly outputCard = viewChild('outputCard', { read: ElementRef<HTMLElement> });

  /** Id of the definition being edited, or null in add mode. */
  readonly journalProcessDefinitionId = signal<string | null>(null);
  /** Owning booking group, needed when adding and used for navigation. */
  readonly bookingGroupId = signal<string>('');
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly notFound = signal(false);

  /** The last loaded/saved record, used for the read-only view and to revert on cancel. */
  private readonly record = signal<JournalProcessDefinition | null>(null);

  /** Name of the owning booking group, resolved from its id (used in add mode). */
  private readonly bookingGroupNameSignal = signal<string>('');

  /** Employment selections available for the "Persoon selectie" dropdown. */
  private readonly employmentSelections = signal<EmploymentSelection[]>([]);

  readonly detailsEditing = signal(false);
  readonly outputEditing = signal(false);

  /**
   * One shared reactive form for the whole record. The template renders the
   * "Details" controls in the first card and the "Uitvoerparameters" controls
   * in the second, but both cards persist the same entity.
   */
  readonly form: FormGroup = this.formBuilder.group(
    {
      // Details
      name: ['', Validators.required],
      bookingDescription: [''],
      institutionFinancialAdministrationNumber: [''],
      bookingNumber: [''],
      journalNumber: [''],
      journalType: [''],
      // Uitvoerparameters
      employmentSelectionId: [null as string | null],
      applyWagePartioning: [false],
      applyRedistribution: [false],
      bookByEmployment: [false],
      bookNegativeValuesIndicator: [false],
      retroactiveCalculationInPayrollPeriod: [false],
      startDate: [defaultStartDate(), Validators.required],
      endDate: [null],
      memoValue: [''],
    },
    { validators: startBeforeEndValidator('startDate', 'endDate') },
  );

  /** Whether we are creating a new definition (no id in the route). */
  readonly isAddMode = computed(() => this.journalProcessDefinitionId() === null);

  /** Error message shown for the einddatum field (start-before-end order validation). */
  endDateError(): string {
    const end = this.form.get('endDate');
    if (
      this.form.hasError('startBeforeEnd') &&
      (this.form.get('startDate')?.touched || end?.touched)
    ) {
      return 'Begindatum moet voor einddatum liggen';
    }
    return '';
  }

  // --- Employment selection options ---------------------------------------------

  /** Combobox options for the "Persoon selectie" dropdown, keyed by id. */
  readonly employmentSelectionOptions = computed<YdsComboboxOption[]>(() => {
    const options = this.employmentSelections().map((selection) => ({
      id: selection.employmentSelectionId,
      label: selection.name,
    }));

    // Keep the label of an already-selected selection visible even if it is not
    // returned by getByContext().
    const selectedId = this.record()?.employmentSelectionId;
    if (selectedId && !options.some((o) => o.id === selectedId)) {
      options.push({ id: selectedId, label: selectedId });
    }

    return options;
  });

  // --- Read-only view helpers ---------------------------------------------------

  readonly nameDisplay = computed(() => this.record()?.name ?? '');
  readonly bookingGroupName = computed(() => this.bookingGroupNameSignal());
  readonly bookingDescriptionDisplay = computed(() => this.record()?.bookingDescription || 'n.v.t.');
  readonly institutionNumberDisplay = computed(
    () => this.record()?.institutionFinancialAdministrationNumber || 'n.v.t.',
  );
  readonly bookingNumberDisplay = computed(() => this.record()?.bookingNumber || 'n.v.t.');
  readonly journalNumberDisplay = computed(() => this.record()?.journalNumber || 'n.v.t.');
  readonly journalTypeDisplay = computed(() => this.record()?.journalType || 'n.v.t.');

  readonly employmentSelectionDisplay = computed(() => {
    const id = this.record()?.employmentSelectionId;
    if (!id) {
      return 'n.v.t.';
    }
    return this.employmentSelectionOptions().find((o) => o.id === id)?.label ?? id;
  });

  readonly applyWagePartioningDisplay = computed(() => Boolean(this.record()?.applyWagePartioning));
  readonly applyRedistributionDisplay = computed(() => Boolean(this.record()?.applyRedistribution));
  readonly bookByEmploymentDisplay = computed(() => Boolean(this.record()?.bookByEmployment));
  readonly bookNegativeValuesDisplay = computed(() =>
    Boolean(this.record()?.bookNegativeValuesIndicator),
  );
  readonly retroactiveCalculationDisplay = computed(() =>
    Boolean(this.record()?.retroactiveCalculationInPayrollPeriod),
  );

  readonly startDateDisplay = computed(() => formatDisplayDate(this.record()?.startDate, 'nl-NL'));
  readonly endDateDisplay = computed(() => {
    const end = this.record()?.endDate;
    return this.isMaxEndDate(end) ? 'n.v.t.' : formatDisplayDate(end, 'nl-NL');
  });
  readonly memoDisplay = computed(() => this.record()?.memo_Value || 'n.v.t.');

  constructor() {
    this.loadEmploymentSelections();

    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.journalProcessDefinitionId.set(id);
      this.loadJournalProcessDefinition(id);
    } else {
      // Add mode: both cards start in edit state (wizard: Details "Volgende"
      // scrolls to Uitvoerparameters, whose "Opslaan" creates the record).
      // The owning booking group is passed through router navigation state.
      const state = this.router.getCurrentNavigation()?.extras?.state ?? history.state;
      const groupId = (state?.['bookingGroupId'] as string) ?? '';
      this.bookingGroupId.set(groupId);
      if (groupId) {
        this.loadBookingGroupName(groupId);
      }
      this.detailsEditing.set(true);
      this.outputEditing.set(true);
    }
  }

  private loadEmploymentSelections(): void {
    this.employmentSelectionService
      .getByContext()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selections) => this.employmentSelections.set(selections));
  }

  private loadBookingGroupName(groupId: string): void {
    this.bookingGroupService
      .getBookingGroupById(groupId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((group) => this.bookingGroupNameSignal.set(group?.bookingGroup_name ?? ''));
  }

  private loadJournalProcessDefinition(id: string): void {
    this.isLoading.set(true);
    this.journalProcessDefinitionService
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (definition) => {
          if (!definition) {
            this.notFound.set(true);
          } else {
            this.record.set(definition);
            this.bookingGroupId.set(definition.bookingGroupId);
            this.loadBookingGroupName(definition.bookingGroupId);
            this.patchForm(definition);
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.notFound.set(true);
          this.isLoading.set(false);
        },
      });
  }

  private patchForm(definition: JournalProcessDefinition): void {
    this.form.patchValue({
      name: definition.name,
      bookingDescription: definition.bookingDescription ?? '',
      institutionFinancialAdministrationNumber:
        definition.institutionFinancialAdministrationNumber ?? '',
      bookingNumber: definition.bookingNumber ?? '',
      journalNumber: definition.journalNumber ?? '',
      journalType: definition.journalType ?? '',
      employmentSelectionId: definition.employmentSelectionId ?? null,
      applyWagePartioning: definition.applyWagePartioning,
      applyRedistribution: definition.applyRedistribution,
      bookByEmployment: definition.bookByEmployment,
      bookNegativeValuesIndicator: definition.bookNegativeValuesIndicator,
      retroactiveCalculationInPayrollPeriod: definition.retroactiveCalculationInPayrollPeriod,
      startDate: toFormDate(definition.startDate) ?? defaultStartDate(),
      endDate: this.isMaxEndDate(definition.endDate) ? null : toFormDate(definition.endDate),
      memoValue: definition.memo_Value ?? '',
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private isMaxEndDate(value: unknown): boolean {
    const date = toFormDate(value as Date | string | null);
    if (!date) {
      return false;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}` === MAX_END_DATE_ISO;
  }

  // --- Details card actions -----------------------------------------------------

  onDetailsCancel(): void {
    if (this.isAddMode()) {
      this.goBack();
      return;
    }

    const current = this.record();
    if (current) {
      this.patchForm(current);
    }
    this.detailsEditing.set(false);
    this.scrollTo(this.detailsCard());
  }

  onDetailsNext(): void {
    if (this.isAddMode()) {
      // "Volgende": require at least the definition name, then advance to the
      // Uitvoerparameters section (creation happens on its "Opslaan").
      const name = this.form.get('name');
      if (name?.invalid) {
        name.markAsTouched();
        return;
      }
      this.scrollTo(this.outputCard());
      return;
    }

    this.save('details');
  }

  // --- Uitvoerparameters card actions -------------------------------------------

  onOutputCancel(): void {
    if (this.isAddMode()) {
      this.goBack();
      return;
    }

    const current = this.record();
    if (current) {
      this.patchForm(current);
    }
    this.outputEditing.set(false);
    this.scrollTo(this.outputCard());
  }

  onOutputNext(): void {
    this.save('output');
  }

  // --- Persistence --------------------------------------------------------------

  private save(card: 'details' | 'output'): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const wasAddMode = this.isAddMode();
    this.isSaving.set(true);

    this.persist()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this.isSaving.set(false);
          if (wasAddMode) {
            if (saved?.journalProcessDefinitionId) {
              this.router.navigate([
                '/config/journalprocessdefinition/edit',
                saved.journalProcessDefinitionId,
              ]);
            } else {
              this.goBack();
            }
            return;
          }

          this.record.set({ ...(this.record() as JournalProcessDefinition), ...saved });
          if (card === 'details') {
            this.detailsEditing.set(false);
            this.scrollTo(this.detailsCard());
          } else {
            this.outputEditing.set(false);
            this.scrollTo(this.outputCard());
          }
        },
        error: () => {
          this.isSaving.set(false);
        },
      });
  }

  /** Builds the full record from the shared form and saves it (add or update). */
  private persist(): Observable<JournalProcessDefinition> {
    const value = this.form.getRawValue() as {
      name: string;
      bookingDescription: string;
      institutionFinancialAdministrationNumber: string;
      bookingNumber: string;
      journalNumber: string;
      journalType: string;
      employmentSelectionId: string | null;
      applyWagePartioning: boolean;
      applyRedistribution: boolean;
      bookByEmployment: boolean;
      bookNegativeValuesIndicator: boolean;
      retroactiveCalculationInPayrollPeriod: boolean;
      startDate: Date | null;
      endDate: Date | null;
      memoValue: string;
    };

    const payload: JournalProcessDefinition = {
      journalProcessDefinitionId: this.journalProcessDefinitionId() ?? '',
      bookingGroupId: this.bookingGroupId(),
      name: value.name,
      bookingDescription: value.bookingDescription || null,
      institutionFinancialAdministrationNumber:
        value.institutionFinancialAdministrationNumber || null,
      bookingNumber: value.bookingNumber || null,
      journalNumber: value.journalNumber || null,
      journalType: value.journalType || null,
      employmentSelectionId: value.employmentSelectionId || null,
      applyWagePartioning: value.applyWagePartioning,
      applyRedistribution: value.applyRedistribution,
      bookByEmployment: value.bookByEmployment,
      bookNegativeValuesIndicator: value.bookNegativeValuesIndicator,
      retroactiveCalculationInPayrollPeriod: value.retroactiveCalculationInPayrollPeriod,
      startDate: (value.startDate ?? defaultStartDate()) as Date,
      endDate: (value.endDate ?? defaultEndDate()) as Date,
      memo_Value: value.memoValue || null,
    };

    const id = this.journalProcessDefinitionId();
    return id
      ? this.journalProcessDefinitionService.updateJournalProcessDefinition(id, payload)
      : this.journalProcessDefinitionService.addJournalProcessDefinition(payload);
  }

  // --- Page actions -------------------------------------------------------------

  private scrollTo(card: ElementRef<HTMLElement> | undefined): void {
    // Deferred so the target card is laid out before we scroll. Using
    // scrollIntoView (rather than a manual window.scrollTo offset) scrolls the
    // actual document scroller regardless of the app shell layout; the sticky
    // header clearance is handled by scroll-margin-top on the card in the scss.
    setTimeout(() => {
      card?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  goBack(): void {
    const groupId = this.bookingGroupId();
    this.router.navigateByUrl(
      groupId
        ? `config/journalprocessdefinition/${groupId}`
        : 'config/journalprocessdefinition',
    );
  }

  onDelete(): void {
    const id = this.journalProcessDefinitionId();
    if (!id) {
      return;
    }

    deleteJournalProcessDefinitionWithConfirm({
      id,
      service: this.journalProcessDefinitionService,
      dialogService: this.dialogService,
      destroyRef: this.destroyRef,
      onDeleted: () => this.goBack(),
    });
  }
}
